import { action, observable, makeObservable } from 'mobx';
import {
  contentLoaders,
  ContentLookup,
  SuccessResponse,
  X1Language,
  X2AgeCategory,
  X3Level,
  X4Unit,
  X5Lesson,
  X6Page,
  X6PageUpdateModel
} from '../../../../domain';
import {
  Control,
  HttpPromiseState,
  newValueForSelection,
  SaveStateHolder,
  validators
} from '../../../../lib/helpers';

export class MovePageStore implements SaveStateHolder {
  readonly pageId: number;
  language: Control<X1Language | null>;
  ageCategory: Control<X2AgeCategory | null>;
  level: Control<X3Level | null>;
  unit: Control<X4Unit | null>;
  lesson: Control<X5Lesson | null>;


  readonly languageOptions: readonly X1Language[];
  @observable.ref
  ageCategoryOptions: readonly X2AgeCategory[];
  @observable.ref
  levelOptions: readonly X3Level[];
  @observable.ref
  unitOptions: readonly X4Unit[];
  @observable.ref
  lessonOptions: readonly X5Lesson[];

  @observable.ref
  save: HttpPromiseState<SuccessResponse> | null;

  constructor(readonly page: X6Page,
              readonly lookup: ContentLookup) {
    makeObservable(this);
    this.pageId = page.Id;
    const lessonId = page.LessonId;
    const lesson = lookup.X5LessonById[lessonId];

    this.language = new Control<X1Language | null>(
      'Language',
      lesson!.Unit!.Level!.AgeCategory!.Language,
      validators.required
    );

    this.language.addValueChangeListener((language) => {
      this.ageCategoryOptions = [];
      if (language !== null)
        this.ageCategoryOptions = language.AgeCategories!;
      const newValue = newValueForSelection(this.ageCategoryOptions, this.ageCategory.value);
      this.ageCategory.setValue(newValue);
    });

    this.ageCategory = new Control<X2AgeCategory | null>(
      'AgeCategory',
      lesson!.Unit!.Level!.AgeCategory,
      validators.required
    );

    this.ageCategory.addValueChangeListener((ageCategory) => {
      this.levelOptions = [];
      if (ageCategory !== null)
        this.levelOptions = ageCategory.Levels!;
      const newValue = newValueForSelection(this.levelOptions, this.level.value);
      this.level.setValue(newValue);
    });

    this.level = new Control<X3Level | null>(
      'Level',
      lesson!.Unit!.Level,
      validators.required
    );

    this.level.addValueChangeListener((level) => {
      this.unitOptions = [];
      if (level !== null)
        this.unitOptions = level.Units!;
      const newValue = newValueForSelection(this.unitOptions, this.unit.value);
      this.unit.setValue(newValue);
    });

    this.unit = new Control<X4Unit | null>(
      'Unit',
      lesson!.Unit,
      validators.required
    );

    this.unit.addValueChangeListener((unit) => {
      this.lessonOptions = [];
      if (unit !== null)
        this.lessonOptions = unit.Lessons!;
      const newValue = newValueForSelection(this.lessonOptions, this.lesson.value);
      this.lesson.setValue(newValue);
    });

    this.lesson = new Control<X5Lesson | null>(
      'Lesson',
      lesson,
      validators.required
    );

    this.languageOptions = lookup.Languages;
    this.ageCategoryOptions = lesson.Unit!.Level!.AgeCategory!.Language!.AgeCategories!;
    this.levelOptions = lesson.Unit!.Level!.AgeCategory!.Levels!;
    this.unitOptions = lesson.Unit!.Level!.Units!;
    this.lessonOptions = lesson.Unit!.Lessons!;

    this.save = null;
  }

  @action
  complete() {
    if (this.save?.isPending === true)
      return;

    this.language.markTouched();
    this.ageCategory.markTouched();
    this.level.markTouched();
    this.unit.markTouched();
    this.lesson.markTouched();

    if (this.lesson.invalid)
      return;

    const model: X6PageUpdateModel = {
      LessonId: this.lesson.value!.Id,
      Name: this.page.Name
    };
    this.save = contentLoaders.x6page.update(this.page.Id, model);
  }
}
